/**
 * Dashboard — Visão geral comercial.
 *
 * Dados reais: reports (funnel/forecast/ranking), opportunities count,
 * activities e inbox — consumidos via useDashboardData.
 *
 * O filtro de período ("Mês/Trimestre/Ano") parametriza as queries reais.
 * O card Azim AI é uma vitrine estática (copiloto "em breve").
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTenantScope } from "../../app/providers/QueryProvider.js";
import { buildQueryKey } from "../../data/queryKeys.js";
import { Icon } from "@azim-crm/icons";
import {
  Button,
  Card,
  Kpi,
  Badge,
  Segmented,
  Progress,
  Page,
  PageHeader,
  Body,
} from "@azim-crm/ui-components";
import { formatMoney } from "../../shared/money/money.js";
import { useDashboardData, type DashboardPeriod } from "../../features/dashboard/hooks.js";
import { CreateOpportunityDialog } from "../../features/opportunities/CreateOpportunityDialog.js";
import type { RankingRow } from "../../features/reports/types.js";
import type { ActivityItem } from "../../features/activities/types.js";
import type { InboxItemData } from "../../features/inbox/types.js";
import styles from "./DashboardPage.module.css";

// ---- Cores para o funil ----------------------------------------------------
const FUNNEL_COLORS = [
  "var(--stage-lead)",
  "var(--stage-qualify)",
  "var(--stage-propose)",
  "var(--stage-negotiate)",
  "var(--stage-won)",
  "var(--stage-lost)",
];

const ACTIVITY_ICONS: Record<string, string> = {
  call:      "phone",
  email:     "mail",
  meeting:   "calendar",
  task:      "check",
  follow_up: "refresh",
};

// ---------------------------------------------------------------------------
// Helpers de estado de atividade → cor/label
// ---------------------------------------------------------------------------

function isActivityOverdue(item: ActivityItem): boolean {
  return item.state === "overdue";
}

function activityDueLabel(item: ActivityItem): string {
  // O campo dueDate vem como ISO string do backend; no mock vem como label legível.
  const raw = item.dueDate;
  if (!raw) return "";
  // Tenta formatar data ISO; se não for ISO retorna como está (mock legível).
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d < today) return "Atrasada";
  if (d < tomorrow) return "Hoje";
  const diff = Math.round((d.getTime() - today.getTime()) / 86_400_000);
  if (diff === 1) return "Amanhã";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

// ---- AI Briefing (vitrine estática) ----------------------------------------
function AIBriefing() {
  const navigate = useNavigate();
  return (
    <div className={styles.aiBriefing}>
      <div className={styles.aiBriefingHeader}>
        <span className={styles.aiIcon} aria-hidden="true">
          <Icon name="sparkles" size={15} />
        </span>
        <span className={styles.aiBriefingTitle}>Azim AI · resumo do dia</span>
        <span style={{ marginLeft: 2 }}>
          <Badge tone="accent" size="sm">em breve</Badge>
        </span>
      </div>
      <p className={styles.aiBriefingText}>
        O copiloto de IA da Azim vai resumir prioridades do seu pipeline,
        alertar riscos de churn e sugerir próximas ações — diretamente aqui.
        Funcionalidade disponível em breve.
      </p>
      <div className={styles.aiBriefingActions}>
        <Button size="sm" variant="ghost" icon="sparkles" onClick={() => navigate("/accounts")}>
          Ver contas em risco
        </Button>
      </div>
    </div>
  );
}

// ---- FunnelCard — funil real com barras horizontais ------------------------
interface FunnelCardProps {
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  funnel: Array<{ stage: string; count: number; value: { amountCents: number; currency: "BRL" | "USD" | "EUR" } }>;
}

function FunnelCard({ loading, error, onRetry, funnel }: FunnelCardProps) {
  const navigate = useNavigate();
  const maxCents = funnel.length > 0 ? Math.max(...funnel.map((f) => f.value.amountCents)) : 1;

  return (
    <Card>
      <div className={styles.sectionTitle}>
        <h2 className={styles.sectionTitleText}>Pipeline por estágio</h2>
        <Button
          variant="ghost"
          size="sm"
          iconRight="arrowRight"
          onClick={() => navigate("/reports")}
        >
          Relatórios
        </Button>
      </div>

      {loading && (
        <div
          role="status"
          style={{ height: 120 }}
          aria-busy="true"
          aria-label="Carregando funil"
          data-skeleton
        />
      )}

      {!loading && error && (
        <div style={{ fontSize: 13, color: "var(--danger)" }} role="alert">
          <p>Não foi possível carregar.</p>
          <button type="button" onClick={onRetry} style={{ cursor: "pointer" }}>Tentar novamente</button>
        </div>
      )}

      {!loading && !error && funnel.length === 0 && (
        <p style={{ color: "var(--fg-3)", fontSize: 13 }}>Sem dados no período.</p>
      )}

      {!loading && !error && funnel.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
          {funnel.map((f, i) => (
            <div key={f.stage} className={styles.funnelRow}>
              <span className={styles.funnelLabel}>{f.stage}</span>
              <div className={styles.funnelTrack}>
                <div
                  className={styles.funnelFill}
                  style={{
                    width: `${Math.max(6, (f.value.amountCents / maxCents) * 100)}%`,
                    background: FUNNEL_COLORS[i] ?? "var(--bg-accent)",
                  }}
                  role="presentation"
                />
              </div>
              <span className={styles.funnelValue}>{formatMoney(f.value)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---- ActivitiesCard — usa dados reais de activitiesRepo --------------------
interface ActivitiesCardProps {
  loading: boolean;
  error: string | null;
  items: readonly ActivityItem[];
}

function ActivitiesCard({ loading, error, items }: ActivitiesCardProps) {
  const navigate = useNavigate();
  const todays = items
    .filter((a) => a.state !== "upcoming")
    .slice(0, 5);

  return (
    <Card>
      <div className={styles.sectionTitle}>
        <h2 className={styles.sectionTitleText}>Atividades de hoje</h2>
        <Button variant="ghost" size="sm" onClick={() => navigate("/activities")}>
          Tudo
        </Button>
      </div>

      {loading && (
        <div
          role="status"
          style={{ height: 80 }}
          aria-busy="true"
          aria-label="Carregando atividades"
          data-skeleton
        />
      )}

      {!loading && error && (
        <p style={{ color: "var(--danger)", fontSize: 13 }} role="alert">
          Não foi possível carregar as atividades.
        </p>
      )}

      {!loading && !error && todays.length === 0 && (
        <p style={{ color: "var(--fg-3)", fontSize: 13 }}>
          Nenhuma atividade pendente hoje.
        </p>
      )}

      {!loading && !error && todays.length > 0 && (
        <div className={styles.activityList}>
          {todays.map((a) => {
            const iconName = ACTIVITY_ICONS[a.type] ?? "check";
            const overdue = isActivityOverdue(a);
            const dueLabel = activityDueLabel(a);
            return (
              <button
                key={a.id}
                type="button"
                className={styles.activityItem}
                onClick={() => navigate("/activities")}
                aria-label={`${a.title} — ${dueLabel}`}
              >
                <span
                  className={[
                    styles.activityIcon,
                    overdue ? styles.activityIconOverdue : styles.activityIconNormal,
                  ].join(" ")}
                  aria-hidden="true"
                >
                  <Icon name={iconName as "check"} size={15} />
                </span>
                <div className={styles.activityInfo}>
                  <div className={styles.activityTitle}>{a.title}</div>
                  {a.accountName && (
                    <div className={styles.activityAccount}>{a.accountName}</div>
                  )}
                </div>
                <span
                  className={[
                    styles.activityDue,
                    overdue ? styles.activityDueOverdue : styles.activityDueNormal,
                  ].join(" ")}
                >
                  {dueLabel}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---- RecentUpdates — usa dados reais de inboxRepo --------------------------
interface RecentUpdatesProps {
  loading: boolean;
  error: string | null;
  items: readonly InboxItemData[];
}

function RecentUpdates({ loading, error, items }: RecentUpdatesProps) {
  const navigate = useNavigate();
  const recent = items.slice(0, 4);

  return (
    <Card>
      <div className={styles.sectionTitle}>
        <h2 className={styles.sectionTitleText}>Atualizações recentes</h2>
        <Button variant="ghost" size="sm" onClick={() => navigate("/inbox")}>
          Inbox
        </Button>
      </div>

      {loading && (
        <div
          role="status"
          style={{ height: 80 }}
          aria-busy="true"
          aria-label="Carregando atualizações"
          data-skeleton
        />
      )}

      {!loading && error && (
        <p style={{ color: "var(--danger)", fontSize: 13 }} role="alert">
          Não foi possível carregar as atualizações.
        </p>
      )}

      {!loading && !error && recent.length === 0 && (
        <p style={{ color: "var(--fg-3)", fontSize: 13 }}>
          Nenhuma atualização recente.
        </p>
      )}

      {!loading && !error && recent.length > 0 && (
        <div className={styles.inboxList}>
          {recent.map((m) => {
            const isAI = m.kind === "ai";
            const displayName = m.accountName ?? m.who ?? "—";
            return (
              <button
                key={m.id}
                type="button"
                className={styles.inboxItem}
                onClick={() => navigate("/inbox")}
                aria-label={`${isAI ? "Azim AI" : displayName} — ${m.preview ?? ""}`}
              >
                {isAI ? (
                  <span className={styles.inboxAiIcon} aria-hidden="true">
                    <Icon name="sparkles" size={14} />
                  </span>
                ) : (
                  <span
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: "var(--bg-subtle)",
                      color: "var(--fg-2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  >
                    {(displayName).slice(0, 2).toUpperCase()}
                  </span>
                )}
                <div className={styles.inboxInfo}>
                  <div className={styles.inboxHeader}>
                    <span className={styles.inboxName}>
                      {isAI ? "Azim AI" : displayName}
                    </span>
                    {m.unread && (
                      <span role="img" className={styles.inboxUnread} aria-label="Não lida" />
                    )}
                  </div>
                  {m.preview && (
                    <div className={styles.inboxPreview}>{m.preview}</div>
                  )}
                </div>
                {m.time && (
                  <span className={styles.inboxTime}>{m.time}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---- RankingCard — usa dados reais de reportsRepo --------------------------
interface RankingCardProps {
  loading: boolean;
  error: string | null;
  rows: readonly RankingRow[];
}

function RankingCard({ loading, error, rows }: RankingCardProps) {
  const navigate = useNavigate();
  const top = rows.slice(0, 5);

  return (
    <Card pad={false}>
      <div style={{ padding: "16px 18px 12px" }}>
        <div className={styles.sectionTitle} style={{ marginBottom: 0 }}>
          <h2 className={styles.sectionTitleText}>Ranking de vendedores</h2>
          <Button
            variant="ghost"
            size="sm"
            iconRight="arrowRight"
            onClick={() => navigate("/reports")}
          >
            Ver relatórios
          </Button>
        </div>
      </div>

      {loading && (
        <div
          role="status"
          style={{ height: 80, margin: "0 18px" }}
          aria-busy="true"
          aria-label="Carregando ranking"
          data-skeleton
        />
      )}

      {!loading && error && (
        <p style={{ padding: "0 18px 16px", color: "var(--danger)", fontSize: 13 }} role="alert">
          Não foi possível carregar o ranking.
        </p>
      )}

      {!loading && !error && top.length === 0 && (
        <p style={{ padding: "0 18px 16px", color: "var(--fg-3)", fontSize: 13 }}>
          Sem dados no período.
        </p>
      )}

      {!loading && !error && top.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table className={styles.oppsTable} aria-label="Ranking de vendedores">
            <thead>
              <tr className={styles.oppsTableHead}>
                <th scope="col">#</th>
                <th scope="col">Vendedor</th>
                <th scope="col">Ganho</th>
                <th scope="col">Negócios</th>
              </tr>
            </thead>
            <tbody>
              {top.map((row) => (
                <tr key={row.repId} className={styles.oppsTableRow}>
                  <td style={{ width: 32, color: "var(--fg-3)" }}>{row.rank}</td>
                  <td>
                    <div className={styles.oppName}>{row.repName}</div>
                  </td>
                  <td>
                    <strong>{formatMoney(row.won)}</strong>
                  </td>
                  <td style={{ color: "var(--fg-3)" }}>{row.deals}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ---- DashboardPage (exported) -----------------------------------------------
export function DashboardPage() {
  const queryClient = useQueryClient();
  const scope = useTenantScope();
  const [period, setPeriod] = useState<DashboardPeriod>("q");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const {
    kpis,
    funnel,
    ranking,
    activities,
    inbox,
    loading,
    error,
    refetch,
  } = useDashboardData(null, period);

  const handlePeriodChange = useCallback((v: string) => {
    setPeriod(v as DashboardPeriod);
  }, []);

  const handleCreateSuccess = useCallback(() => {
    // Invalida queries do dashboard usando o prefixo [tenantId, buId, area] correto.
    // Invalidar apenas ['dashboard-reports'] não casa com as chaves reais geradas
    // por useTenantQuery ([tenantId, buId, 'dashboard-reports', params]) — a lista
    // ficaria stale até o staleTime (30 s) expirar (FINDING portal-pages #7).
    const reportsKey = buildQueryKey("dashboard-reports", scope.tenantId, scope.businessUnitId);
    const oppsKey = buildQueryKey("dashboard-opps-count", scope.tenantId, scope.businessUnitId);
    void queryClient.invalidateQueries({ queryKey: reportsKey });
    void queryClient.invalidateQueries({ queryKey: oppsKey });
    setCreateDialogOpen(false);
  }, [queryClient, scope]);

  // Derivação segura de quotaPct (sem fonte nesta onda → sem barra de progresso)
  const wonQuarterMoney = kpis?.wonQuarter ?? null;
  const quotaMoney = kpis?.quotaQuarter ?? null;
  const quotaPct =
    wonQuarterMoney && quotaMoney && quotaMoney.amountCents > 0
      ? Math.round((wonQuarterMoney.amountCents / quotaMoney.amountCents) * 100)
      : null;

  return (
    <Page>
      <PageHeader
        eyebrow="Visão geral"
        title="Visão geral comercial"
        sub="Dados reais do pipeline"
        actions={
          <>
            <Segmented
              value={period}
              onChange={handlePeriodChange}
              size="sm"
              options={[
                { value: "m", label: "Mês" },
                { value: "q", label: "Trimestre" },
                { value: "y", label: "Ano" },
              ]}
            />
            <Button
              variant="primary"
              icon="plus"
              onClick={() => setCreateDialogOpen(true)}
            >
              Nova oportunidade
            </Button>
          </>
        }
      />
      <Body>
        {/* KPI row */}
        <div className={styles.kpiGrid}>
          <Kpi
            label="Pipeline aberto"
            value={
              loading ? "…" :
              error ? "—" :
              kpis?.pipelineTotal ? formatMoney(kpis.pipelineTotal) : "—"
            }
            icon="target"
            lead
            {...(kpis ? { sub: `${kpis.openCount} oportunidades ativas` } : {})}
          />
          <Kpi
            label="Previsão ponderada"
            value={
              loading ? "…" :
              error ? "—" :
              kpis?.weighted ? formatMoney(kpis.weighted) : "—"
            }
            icon="trendUp"
            accent
            sub="Forecast ponderado no período"
          />
          <Kpi
            label="Ganho no período"
            value={
              loading ? "…" :
              error ? "—" :
              kpis?.wonQuarter ? formatMoney(kpis.wonQuarter) : "—"
            }
            icon="dollar"
            {...(quotaPct !== null ? {
              spark: (
                <div className={styles.quotaRow}>
                  <Progress
                    value={quotaPct}
                    tone="success"
                    aria-label={`Meta: ${quotaPct}% atingida`}
                  />
                  <span className={styles.quotaLabel}>
                    {quotaPct}% da meta
                  </span>
                </div>
              ),
            } : {})}
          />
          <Kpi
            label="Win rate · ciclo médio"
            value={
              loading ? "…" :
              error ? "—" :
              kpis?.winRate !== null && kpis?.winRate !== undefined
                ? `${Math.round(kpis.winRate * 100)}%`
                : "—"
            }
            icon="refresh"
            sub={
              kpis?.avgCycleDays !== null && kpis?.avgCycleDays !== undefined
                ? `Ciclo médio de ${kpis.avgCycleDays} dias`
                : "Sem dados de ciclo nesta onda"
            }
          />
        </div>

        {/* AI briefing (vitrine estática) */}
        <AIBriefing />

        {/* Funil por estágio. O card "Pipeline aberto" que ficava à esquerda saiu: repetia o
            total do KPI do topo, e o valor precisa de um lugar só. */}
        <div className={styles.pipelineGrid}>
          <FunnelCard
            loading={loading}
            error={error}
            onRetry={refetch}
            funnel={funnel}
          />
        </div>

        {/* Atividades + inbox */}
        <div className={styles.twoColGrid}>
          <RankingCard
            loading={loading}
            error={error}
            rows={ranking}
          />
          <div className={styles.rightCol}>
            <ActivitiesCard
              loading={loading}
              error={error}
              items={activities?.items ?? []}
            />
            <RecentUpdates
              loading={loading}
              error={error}
              items={inbox}
            />
          </div>
        </div>
      </Body>

      {/* Dialog de criação de oportunidade */}
      <CreateOpportunityDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </Page>
  );
}
